import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
    SparklesIcon, 
    FireIcon, 
    HeartIcon,
    TagIcon,
    FunnelIcon,
    Squares2X2Icon,
    ListBulletIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as SolidHeartIcon } from '@heroicons/react/24/solid';
import ProductCard from '../components/ProductCard';
import ProductCardSkeleton from '../components/ProductCardSkeleton';
import { useFavorites } from '../context/FavoritesContext';
import api from '../utils/api';

function CollectionsPage() {
    const { collectionSlug } = useParams();
    const navigate = useNavigate();
    const { favorites, addToFavorites, removeFromFavorites } = useFavorites();
    
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [viewMode, setViewMode] = useState('grid');
    const [sortBy, setSortBy] = useState('newest');
    const [filterBy, setFilterBy] = useState('all');

    // Koleksiyon tanımları
    const collections = {
        'yeni-sezon': {
            title: 'Yeni Sezon Koleksiyonu',
            description: 'En yeni ve trend ürünlerimizi keşfedin',
            icon: SparklesIcon,
            color: 'from-brand-green-light to-brand-green',
            filter: { isNew: true }
        },
        'en-cok-satanlar': {
            title: 'En Çok Satanlar',
            description: 'Müşterilerimizin en çok tercih ettiği ürünler',
            icon: FireIcon,
            color: 'from-green-600 to-brand-green',
            filter: { bestseller: true }
        },
        'indirimli-urunler': {
            title: 'İndirimli Ürünler',
            description: 'Özel fiyatlarla kaliteli ürünler',
            icon: TagIcon,
            color: 'from-brand-green-light to-green-700',
            filter: { sale: true }
        },
        'minela-kids': {
            title: 'Minela Kids Özel Koleksiyonu',
            description: 'Özenle tasarlanmış özel koleksiyonumuz',
            icon: HeartIcon,
            color: 'from-brand-green to-brand-green-light',
            filter: { collection: 'minela-kids' }
        }
    };

    const currentCollection = collections[collectionSlug] || collections['yeni-sezon'];

    useEffect(() => {
        fetchProducts();
    }, [collectionSlug, sortBy, filterBy]);

    const fetchProducts = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                ...currentCollection.filter,
                sort: sortBy,
                filter: filterBy,
                limit: 50
            });

            const response = await api(`/api/products?${params}`);
            setProducts(response.products || []);
        } catch (error) {
            console.error('Ürünler yüklenirken hata:', error);
            setProducts([]);
        } finally {
            setLoading(false);
        }
    };

    const handleFavoriteToggle = (productId) => {
        if (favorites.includes(productId)) {
            removeFromFavorites(productId);
        } else {
            addToFavorites(productId);
        }
    };

    const sortOptions = [
        { value: 'newest', label: 'En Yeni' },
        { value: 'oldest', label: 'En Eski' },
        { value: 'price-low', label: 'Fiyat: Düşükten Yükseğe' },
        { value: 'price-high', label: 'Fiyat: Yüksekten Düşüğe' },
        { value: 'name-asc', label: 'İsim: A-Z' },
        { value: 'name-desc', label: 'İsim: Z-A' }
    ];

    const filterOptions = [
        { value: 'all', label: 'Tümü' },
        { value: 'kiz-cocuk', label: 'Kız Çocuk' },
        { value: 'erkek-cocuk', label: 'Erkek Çocuk' },
        { value: 'bebek', label: 'Bebek' },
        { value: 'ayakkabi', label: 'Ayakkabı' },
        { value: 'aksesuar', label: 'Aksesuar' }
    ];

    const IconComponent = currentCollection.icon;

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
            {/* Hero Section */}
            <div className={`bg-gradient-to-r ${currentCollection.color} text-white py-12 sm:py-16`}>
                <div className="container mx-auto px-4 text-center">
                    <div className="flex items-center justify-center mb-4">
                        <IconComponent className="w-12 h-12 sm:w-16 sm:h-16 mr-4" />
                        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold">
                            {currentCollection.title}
                        </h1>
                    </div>
                    <p className="text-lg sm:text-xl opacity-90 max-w-2xl mx-auto">
                        {currentCollection.description}
                    </p>
                </div>
            </div>

            <div className="container mx-auto px-4 py-8">
                
                {/* Koleksiyon Navigasyonu */}
                <div className="mb-8">
                    <div className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100">
                        <h2 className="text-xl font-bold text-gray-800 mb-4">Diğer Koleksiyonlar</h2>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                            {Object.entries(collections).map(([slug, collection]) => {
                                const CollectionIcon = collection.icon;
                                return (
                                    <Link
                                        key={slug}
                                        to={`/collections/${slug}`}
                                        className={`p-4 rounded-lg border-2 transition-all duration-300 ${
                                            collectionSlug === slug
                                                ? 'border-brand-green bg-brand-green text-white shadow-lg'
                                                : 'border-gray-200 bg-white text-gray-700 hover:border-brand-green hover:shadow-md'
                                        }`}
                                    >
                                        <div className="text-center">
                                            <CollectionIcon className="w-8 h-8 mx-auto mb-2" />
                                            <div className="font-bold text-sm">{collection.title}</div>
                                        </div>
                                    </Link>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Filtreler ve Sıralama */}
                <div className="mb-6">
                    <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-6 border border-gray-100">
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                            
                            {/* Sol taraf - Filtreler */}
                            <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
                                <div className="flex items-center gap-2">
                                    <FunnelIcon className="w-5 h-5 text-gray-600" />
                                    <select
                                        value={filterBy}
                                        onChange={(e) => setFilterBy(e.target.value)}
                                        className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-green focus:border-transparent"
                                    >
                                        {filterOptions.map(option => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="flex items-center gap-2">
                                    <span className="text-sm text-gray-600">Sırala:</span>
                                    <select
                                        value={sortBy}
                                        onChange={(e) => setSortBy(e.target.value)}
                                        className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-green focus:border-transparent"
                                    >
                                        {sortOptions.map(option => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Sağ taraf - Görünüm modu ve sonuç sayısı */}
                            <div className="flex items-center gap-4 w-full sm:w-auto justify-between sm:justify-end">
                                <span className="text-sm text-gray-600">
                                    {products.length} ürün bulundu
                                </span>
                                
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setViewMode('grid')}
                                        className={`p-2 rounded-lg transition-colors ${
                                            viewMode === 'grid'
                                                ? 'bg-brand-green text-white'
                                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                        }`}
                                    >
                                        <Squares2X2Icon className="w-5 h-5" />
                                    </button>
                                    <button
                                        onClick={() => setViewMode('list')}
                                        className={`p-2 rounded-lg transition-colors ${
                                            viewMode === 'list'
                                                ? 'bg-brand-green text-white'
                                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                        }`}
                                    >
                                        <ListBulletIcon className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Ürün Listesi */}
                <div className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100">
                    {loading ? (
                        <div className={`grid gap-6 ${
                            viewMode === 'grid' 
                                ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' 
                                : 'grid-cols-1'
                        }`}>
                            {Array.from({ length: 8 }).map((_, index) => (
                                <ProductCardSkeleton key={index} />
                            ))}
                        </div>
                    ) : products.length > 0 ? (
                        <div className={`grid gap-6 ${
                            viewMode === 'grid' 
                                ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' 
                                : 'grid-cols-1'
                        }`}>
                            {products.map((product) => (
                                <div key={product.id} className="relative">
                                    <ProductCard 
                                        product={product} 
                                        viewMode={viewMode}
                                    />
                                    
                                    {/* Favori Butonu */}
                                    <button
                                        onClick={() => handleFavoriteToggle(product.id)}
                                        className="absolute top-3 right-3 p-2 bg-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 z-10"
                                    >
                                        {favorites.includes(product.id) ? (
                                            <SolidHeartIcon className="w-5 h-5 text-red-500" />
                                        ) : (
                                            <HeartIcon className="w-5 h-5 text-gray-400 hover:text-red-500" />
                                        )}
                                    </button>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-12">
                            <div className="w-24 h-24 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                                <IconComponent className="w-12 h-12 text-gray-400" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-800 mb-2">
                                Bu koleksiyonda ürün bulunamadı
                            </h3>
                            <p className="text-gray-600 mb-6">
                                Farklı filtreler deneyebilir veya diğer koleksiyonlarımıza göz atabilirsiniz.
                            </p>
                            <Link
                                to="/products"
                                className="inline-flex items-center px-6 py-3 bg-brand-green text-white font-bold rounded-lg hover:bg-brand-green-light transition-colors"
                            >
                                Tüm Ürünleri Görüntüle
                            </Link>
                        </div>
                    )}
                </div>

                {/* Alt Bilgi */}
                <div className="mt-8 bg-white rounded-2xl shadow-xl p-6 border border-gray-100">
                    <div className="text-center">
                        <h3 className="text-xl font-bold text-gray-800 mb-2">
                            {currentCollection.title} Hakkında
                        </h3>
                        <p className="text-gray-600 max-w-2xl mx-auto">
                            Minela Kids olarak, çocuklarınızın konforunu ve stilini ön planda tutarak 
                            özenle seçilmiş ürünlerimizi sizlerle buluşturuyoruz. 
                            Kaliteli kumaşlar ve modern tasarımlarla çocuklarınızın her anını özel kılıyoruz.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default CollectionsPage;